import React, { useRef } from 'react';
import { SwiperSlide } from 'swiper/react';
import PopularLocationsSwiper from '@components/PopularLocations/PopularLocationsSwiper';
import ImageFallback from '@components/common/Image/ImageFallback';
import { getCdnImageUrl } from '@utils/cloudflare';
import HotelData from '@customTypes/hotel-data';
import RatingNumber from '@components/Rating/RatingNumber';
import RatingTag from '@components/Rating/RatingTag';
import ButtonPrimary from '@components/common/ButtonPrimary';
import useIsMobile from '@hooks/useIsMobile';
import RoutingPath from '@constants/routingPath';

export default function CruiseReviews({
  cruiseReviewsHotels,
  cruiseReviewsData,
  showHotelSection,
  isNclPage,
}: any) {
  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const swiperNavigation = {
    prevRef: navigationPrevRef,
    nextRef: navigationNextRef,
  };

  return (
    <div
      className={`browse-hotels cruise-reviews mt-0 bg-rp-primary-new w-full d:rounded-xl ${
        isNclPage ? 'd:h-365' : 'd:h-496'
      }`}
    >
      <div className="hidden items-end justify-end p-6 absolute mt-14 ml-24 pl-7 d:flex d:rounded-xl">
        <div className="flex h-9 items-center space-x-4">
          <div ref={navigationPrevRef} className="swiper-button-prev" />
          <div ref={navigationNextRef} className="swiper-button-next" />
        </div>
      </div>

      <div className="d:rounded-xl">
        <PopularLocationsSwiper
          navigation={swiperNavigation}
          spaceBetween={0}
          isPaginated={isMobile}
        >
          <div className="d:mx-auto">
            {cruiseReviewsHotels && cruiseReviewsHotels?.length ? (
              cruiseReviewsHotels.map((hotel: HotelData) => (
                <SwiperSlide key={hotel.id}>
                  <div
                    className={`bg-rp-primary-new flex flex-col items-center px-4 pb-12 justify-between d:justify-center d:min-h-full d:pb-0 d:px-32 d:flex-row d:rounded-xl ${
                      isNclPage ? 'd:h-365' : 'd:h-496'
                    }`}
                  >
                    <div
                      className={`text-lg font-rp-gelica-light font-extralight tracking-0.3 flex flex-col d:mr-20 ${
                        showHotelSection ? 'd:w-3/5' : 'd:w-full'
                      }`}
                    >
                      <p className="text-white mt-12 leading-22px">
                        &quot;
                        {(cruiseReviewsData as any)[hotel.id]
                          ? (cruiseReviewsData as any)[hotel.id].review
                          : ''}
                        &quot;
                      </p>
                      <p className="text-white mt-5 leading-22px text-sm">
                        -{' '}
                        {(cruiseReviewsData as any)[hotel.id]
                          ? (cruiseReviewsData as any)[hotel.id].reviewer
                          : ''}
                      </p>
                    </div>
                    {showHotelSection && (
                      <div className="flex flex-col bg-white px-5 rounded-xl pb-8 pt-3 mt-8 w-full d:mt-0 d:w-1/3">
                        <div className="flex justify-between items-start pt-5 mt-px pb-6">
                          <div className="flex flex-col items-start justify-center">
                            <p className="opacity-50 text-sm">{`${hotel.city_name}, ${hotel.code}`}</p>
                            <h2 className="text-17 pr-5 font-rp-pn-semi-bold text-rp-primary-black tracking-0.3">
                              {hotel.name}
                            </h2>
                            <div className="flex justify-items-start cursor-pointer items-center pt-2">
                              {hotel.avg_rating && (
                                <>
                                  <RatingNumber rating={+hotel.avg_rating} size="md" />
                                  <RatingTag isSeoPage rating={+hotel.avg_rating} />
                                </>
                              )}
                              <div className="text-custom-gray-4 bg-gray-600 ml-1 mr-1 w-0.5 h-0.5 rounded-5 opacity-80" />
                              <div className="flex items-center text-sm leading-4 text-custom-gray-4 font-rp-pn-regular">
                                {`${hotel.reviews_count} reviews`}
                              </div>
                            </div>
                          </div>
                          <div className="min-w-102 h-74px">
                            <ImageFallback
                              src={getCdnImageUrl(320, hotel.image_desktop, '', 100)}
                              blurDataUrl={getCdnImageUrl(32, hotel.image_desktop, '', 10)}
                              alt={hotel.name}
                              className="rounded-xl"
                              height={74}
                              width={104}
                              loading="eager"
                            />
                          </div>
                        </div>
                        <div className="flex justify-between border-t border-b border-rp-gray-divider items-center h-auto py-3 mt-0 w-full mb-5">
                          <p>{hotel.best_price_product.name}</p>
                          <p>Starting at ${hotel.best_price_product.price}</p>
                        </div>
                        <div className="h-12 d:mb-2">
                          <ButtonPrimary
                            onClick={() => window.open(`${RoutingPath.DLP}/${hotel.url}`)}
                          >
                            Book Now
                          </ButtonPrimary>
                        </div>
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              ))
            ) : (
              <SwiperSlide onClick={() => null} />
            )}
          </div>
        </PopularLocationsSwiper>
      </div>
    </div>
  );
}

CruiseReviews.defaultProps = {
  showHotelSection: true,
  isNclPage: false,
};
